import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Bandeau, Champ } from './Champs'
import { jourCourt, moisAnnee, montant as formaterMontant } from '../formats'
import { ErreurApi } from '../api'
import { EcranErreur } from './Erreurs'
import {
  bail as chargerBail,
  confirmerBail,
  declarerSigne,
  DEMARCHES,
  engendrerModele,
  marquerDemarche,
  mesBaux,
  telechargerModeleBail,
  type Bail,
  type DemarcheBail,
  type DetailBail,
  type PartieAuBail,
  type StatutDemarche,
} from '../apiBail'
import { interpoler, messageErreur, NOMS_DE_LANGUE, TEXTES, type Langue } from '../i18n'
import { Bloc, Bouton, EtatVide, Icone, LienBouton, Puce, SqueletteListe, SquelettePage } from '../ui'
import { useVille } from '../VilleContexte'
import { useLangue, useTextes } from '../LangueContexte'

/**
 * Le bien d'un bail : « Hay Al Karam, Salé Al Jadida ».
 *
 * L'API rend le quartier et le CODE de la ville, séparés (L13) ; le libellé vient du référentiel,
 * dans la langue du lecteur. Composer ici, et nulle part ailleurs : trois écrans le montrent.
 */
export function libelleBien(
  bail: { propertyLabel: string | null; propertyCityCode: string | null },
  libelleVille: (code: string | null | undefined) => string,
): string {
  if (bail.propertyLabel === null) return '—'
  if (bail.propertyCityCode === null) return bail.propertyLabel

  return `${bail.propertyLabel}, ${libelleVille(bail.propertyCityCode)}`
}

/**
 * Formatage d'un montant.
 *
 * Règle 9 du §6 de la conception L10 : `Intl`, jamais un format écrit à la main. Un échéancier est
 * un tableau de nombres — c'est le pire endroit pour un séparateur décimal figé en français dans
 * une interface arabe.
 */
export function montant(langue: Langue, valeur: number, devise: string): string {
  // Deux decimales ici, contrairement au catalogue : une quittance porte des centimes, un loyer
  // affiche sur une carte n'en porte jamais.
  return formaterMontant(valeur, devise, langue, 2)
}

/** Une date sans heure — échéance, période, signature. */
export function jour(langue: Langue, iso: string): string {
  return jourCourt(iso, langue)
}

/**
 * « 01/07/2026 → 30/06/2027 » : la flèche d'une période suit le sens de lecture. Le glyphe ne se
 * retourne pas seul en arabe, et une flèche qui pointe vers le début raconte l'inverse.
 */
export function periode(langue: Langue, debut: string, fin: string): string {
  return `${jour(langue, debut)} ${langue === 'ar' ? '←' : '→'} ${jour(langue, fin)}`
}

/**
 * Le mois d'une période, sans le jour : c'est ainsi qu'on parle d'un loyer.
 *
 * Les trois délèguent à `formats.ts` depuis L10c. Ils passaient `langue` telle quelle à `Intl`,
 * donc `'ar'` : les chiffres sortaient en arabe oriental (٢٥٥٠), que le Maroc n'écrit pas. La
 * locale marocaine vit désormais à un seul endroit.
 */
export function mois(langue: Langue, iso: string): string {
  return moisAnnee(iso, langue)
}

/**
 * Mes baux — revu le 15/09/2026 sur la maquette `docs/ui/pwa/mes-baux.html`.
 *
 * <b>Deux sections, une carte par bail.</b> « Comme locataire » et « Comme bailleur » ne sont plus
 * des onglets mais deux listes l'une sous l'autre : les deux rôles ne lisent pas la même chose, et
 * quelqu'un qui a les deux n'a pas à basculer. Le rôle se lit sur `landlordUserId`, jamais sur la
 * liste d'origine — `asLandlord=false` rend les baux dont on est PARTIE, bailleur compris.
 *
 * <b>La carte dit ce qu'on vient chercher</b> : les faits du contrat, l'état, et pour un bail en
 * cours de signature, où l'on en est et ce qui reste à faire — au locataire de confirmer, ici,
 * sans ouvrir le bail. La plateforme n'est pas partie au contrat : elle fournit un modèle et une
 * trace, et l'écran le dit en une ligne.
 */
export function MesBaux({ langue, moi }: { langue: Langue; moi: string | null }) {
  const t = TEXTES[langue]
  const ville = useVille()
  const [liste, setListe] = useState<Bail[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState<string | null>(null)

  const charger = useCallback(() => {
    setErreur(null)

    void Promise.all([mesBaux(false), mesBaux(true)])
      .then((pages) => {
        const vus = new Map<string, Bail>()

        for (const page of pages) {
          for (const bail of page.items) {
            vus.set(bail.id, bail)
          }
        }

        setListe([...vus.values()].sort((a, b) => rang(a) - rang(b) || b.startDate.localeCompare(a.startDate)))
      })
      .catch((cause: unknown) =>
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal')),
      )
  }, [langue])

  useEffect(charger, [charger])

  const confirmer = async (id: string) => {
    setOccupe(id)
    setErreur(null)

    try {
      await confirmerBail(id)
      charger()
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setOccupe(null)
    }
  }

  const commeLocataire = liste?.filter((bail) => moi === null || bail.landlordUserId !== moi) ?? []
  const commeBailleur = liste?.filter((bail) => moi !== null && bail.landlordUserId === moi) ?? []

  return (
    <section className="mes-baux">
      <div className="page-entete">
        <h2>{t.mesBaux}</h2>
        <p>{t.mesBauxIntro}</p>
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {liste === null && erreur === null && <SqueletteListe />}
      {liste !== null && liste.length === 0 && (
        <EtatVide icone="cle" titre={t.aucunBail}>
          {t.mesBauxVideAide}
        </EtatVide>
      )}

      {commeLocataire.length > 0 && (
        <div className="mes-baux__groupe">
          <h3>{t.commeLocataire}</h3>
          {commeLocataire.map((bail) => (
            <CarteBail
              key={bail.id}
              t={t}
              langue={langue}
              libelleVille={ville.libelle}
              bail={bail}
              role="locataire"
              occupe={occupe === bail.id}
              onConfirmer={() => void confirmer(bail.id)}
            />
          ))}
        </div>
      )}

      {commeBailleur.length > 0 && (
        <div className="mes-baux__groupe">
          <h3>{t.commeBailleur}</h3>
          {commeBailleur.map((bail) => (
            <CarteBail
              key={bail.id}
              t={t}
              langue={langue}
              libelleVille={ville.libelle}
              bail={bail}
              role="bailleur"
              occupe={occupe === bail.id}
              onConfirmer={() => void confirmer(bail.id)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

/** Ce qui attend un geste d'abord, puis ce qui est en cours, puis ce qui se signe, puis le passé. */
function rang(bail: Bail): number {
  if (bail.awaitingMyConfirmation) return 0
  if (bail.status === 'Active') return 1
  if (bail.status === 'Draft' || bail.status === 'PendingSignature') return 2

  return 3
}

/**
 * Une carte de bail.
 *
 * En cours de signature, la carte montre <b>les étapes</b> plutôt que les faits : ce qu'on
 * regarde alors, c'est où l'on en est. Les faits reviennent dès que le bail est actif — et la
 * fiche les a toujours.
 */
function CarteBail({
  t,
  langue,
  libelleVille,
  bail,
  role,
  occupe,
  onConfirmer,
}: {
  t: (typeof TEXTES)['fr']
  langue: Langue
  libelleVille: (code: string | null | undefined) => string
  bail: Bail
  role: 'locataire' | 'bailleur'
  occupe: boolean
  onConfirmer: () => void
}) {
  const enSignature = bail.status === 'Draft' || bail.status === 'PendingSignature'
  const sousTitre =
    role === 'bailleur' && bail.tenantDisplayNames.length > 0
      ? interpoler(t.mesBauxAvec, { noms: bail.tenantDisplayNames.join(', ') })
      : libelleTypeBail(t, bail)

  return (
    <Bloc
      titre={
        <div>
          <h4>{libelleBien(bail, libelleVille)}</h4>
          <p className="secondaire">{sousTitre}</p>
        </div>
      }
      actions={<Puce ton={tonStatutBail(bail.status)}>{libelleStatutBail(t, bail.status)}</Puce>}
    >
      {bail.awaitingMyConfirmation && (
        <Bandeau ton="attention" icone={<Icone nom="alerte" />}>
          <b>{t.bailSignatureAttendue}</b>{' '}
          {interpoler(t.mesBauxConfirmations, { n: bail.confirmedTenantCount, total: bail.tenantCount }, langue)}
        </Bandeau>
      )}

      {enSignature ? (
        <ol className="etapes">
          <li className="est-faite">
            <span className="etapes__puce">✓</span> {t.mesBauxEtapeCandidature}
          </li>
          <li className={bail.status === 'Draft' ? 'est-active' : 'est-faite'}>
            <span className="etapes__puce">{bail.status === 'Draft' ? '2' : '✓'}</span> {t.mesBauxEtapeSignature}
          </li>
          <li className={bail.status === 'PendingSignature' ? 'est-active' : undefined}>
            <span className="etapes__puce">3</span>{' '}
            {t.mesBauxEtapeConfirmation}
            {bail.status === 'PendingSignature' && (
              <span className="tertiaire">
                {' '}
                {interpoler(t.mesBauxConfirmationsCourt, { n: bail.confirmedTenantCount, total: bail.tenantCount }, langue)}
              </span>
            )}
          </li>
          <li>
            <span className="etapes__puce">4</span> {t.mesBauxEtapeActif}
          </li>
        </ol>
      ) : (
        <dl className="fiche__lignes">
          <div className="fiche__ligne">
            <dt>{t.bailPeriode}</dt>
            <dd>{periode(langue, bail.startDate, bail.endDate)}</dd>
          </div>
          <div className="fiche__ligne">
            <dt>{t.bailLoyer}</dt>
            <dd>
              <bdi>{montant(langue, bail.rentAmount, bail.currencyCode)}</bdi>
            </dd>
          </div>
          <div className="fiche__ligne">
            <dt>{t.bailCharges}</dt>
            <dd>
              <bdi>{montant(langue, bail.chargesAmount, bail.currencyCode)}</bdi>
            </dd>
          </div>
          <div className="fiche__ligne">
            <dt>{t.bailDepot}</dt>
            <dd>
              <bdi>{montant(langue, bail.securityDeposit, bail.currencyCode)}</bdi>
            </dd>
          </div>
          <div className="fiche__ligne">
            <dt>{t.bailEcheanceMensuelle}</dt>
            <dd>{interpoler(t.mesBauxJourEcheance, { jour: bail.paymentDueDayOfMonth }, langue)}</dd>
          </div>
          <div className="fiche__ligne">
            <dt>{t.bailPreavis}</dt>
            <dd>{interpoler(t.mesBauxPreavisJours, { n: bail.noticePeriodDays }, langue)}</dd>
          </div>
        </dl>
      )}

      <div className="rangee rangee--serree">
        {bail.awaitingMyConfirmation && (
          <Bouton taille="sm" charge={occupe} onClick={onConfirmer}>
            {t.bailConfirmerSignature}
          </Bouton>
        )}

        {enSignature ? (
          <LienBouton to={`/baux/${bail.id}`} apparence={bail.awaitingMyConfirmation ? 'secondaire' : 'principal'} taille="sm">
            {t.mesBauxVoirDetail}
          </LienBouton>
        ) : (
          <>
            <LienBouton to={`/baux/${bail.id}`} taille="sm">
              {t.mesBauxOuvrir}
            </LienBouton>
            <LienBouton to={`/echeances?bail=${bail.id}`} apparence="secondaire" taille="sm">
              {t.echeancier}
            </LienBouton>
            <LienBouton to="/quittances" apparence="discret" taille="sm">
              {t.quittances}
            </LienBouton>
          </>
        )}
      </div>
    </Bloc>
  )
}

function tonStatutBail(statut: Bail['status']): 'succes' | 'attention' | 'danger' | undefined {
  switch (statut) {
    case 'Active':
      return 'succes'
    case 'Draft':
    case 'PendingSignature':
      return 'attention'
    case 'Cancelled':
      return 'danger'
    default:
      return undefined
  }
}

/**
 * La fiche du bail — revue le 15/09/2026 sur la maquette `docs/ui/pwa/bail.html`.
 *
 * <b>Deux colonnes.</b> Le contrat, ses parties et ses démarches d'un côté ; de l'autre, en
 * aparté, les documents et la fin du bail. L'échéancier n'est plus embarqué ici : il a son écran,
 * qui s'ouvre sur ce bail (`/echeances?bail=`), et une fiche qui le répétait faisait deux tableaux
 * pour un seul fait.
 *
 * <b>Chaque ligne de démarche est un fait déclaré, daté et attribué</b> — pas une case. La liste
 * dit qui a déclaré quoi et quand ; l'auteur est toujours une partie au bail, donc nommé sans
 * second appel. Le suivi n'engage pas la plateforme : la loi 67-12 impose ou recommande, l'écran
 * trace.
 *
 * <b>Le modèle de bail est dit tel qu'il est</b> : engendré à une date, dans une langue, et
 * provisoire tant que le gabarit n'a pas été relu — l'API le sait, l'écran ne l'invente pas.
 */
export function DetailBailEcran({ langue, moi }: { langue: Langue; moi: string | null }) {
  const { id } = useParams<{ id: string }>()
  const t = TEXTES[langue]
  const ville = useVille()
  const [detail, setDetail] = useState<DetailBail | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  /** La cause de l'échec du CHARGEMENT — un écran entier, là où une action ratée est un bandeau. */
  const [echec, setEchec] = useState<unknown>(null)
  /** L'action en cours — `confirmer`, `signer`, `modele` ou le code d'une démarche. */
  const [occupe, setOccupe] = useState<string | null>(null)
  const [dateSignature, setDateSignature] = useState(() => new Date().toISOString().slice(0, 10))

  const charger = useCallback(() => {
    if (id === undefined) {
      return
    }

    setEchec(null)

    void chargerBail(id)
      .then(setDetail)
      .catch((cause: unknown) => setEchec(cause ?? new Error()))
  }, [id])

  useEffect(charger, [charger])

  const agir = useCallback(
    async (cle: string, action: () => Promise<unknown>, recharger = true) => {
      setErreur(null)
      setOccupe(cle)

      try {
        await action()

        if (recharger) charger()
      } catch (cause: unknown) {
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
      } finally {
        setOccupe(null)
      }
    },
    [charger, langue],
  )
  const telechargerSansRecharger = (cle: string, action: () => Promise<unknown>) => agir(cle, action, false)

  // Revu le 16/09/2026 (`docs/ui/pwa/introuvable.html`, état 3) : un bail que l'API ne rend pas
  // — inexistant, ou dont on n'est pas partie, l'API ne distingue pas et c'est voulu — est un état
  // vide qui le dit, suivi de SES baux : c'est presque toujours l'un d'eux qu'on cherchait.
  if (echec !== null && detail === null) {
    return (
      <section className="bail">
        <nav className="fil-ariane" aria-label={t.annonceFilAriane}>
          <Link to="/mes-baux">{t.mesBaux}</Link>
          <Icone nom="chevron" taille="sm" miroir />
          <span aria-current="page">{t.bailContrat}</span>
        </nav>
        <EcranErreur cause={echec} objet="bail" onReessayer={charger} suite={<MesBauxResume moi={moi} />} />
      </section>
    )
  }

  if (detail === null || id === undefined) {
    return <SquelettePage />
  }

  const bail = detail.lease
  const bien = libelleBien(bail, ville.libelle)
  const estBailleur = moi !== null && bail.landlordUserId === moi
  const maPartie = detail.parties.find((partie) => partie.userId === moi && partie.leftOn === null)
  const jeDoisConfirmer =
    bail.status === 'PendingSignature' && maPartie?.role === 'Tenant' && maPartie.confirmedAt === null
  // « 4 300 MAD (2 mois) » : le rapport au loyer n'est dit que s'il tombe juste — c'est lui que
  // la loi plafonne, et un « 1,86 mois » ne renseigne personne.
  const moisDepot = bail.rentAmount > 0 ? detail.securityDeposit / bail.rentAmount : 0
  const demarches = [...detail.checklist].sort(
    (a, b) => rangDemarche(a.itemCode) - rangDemarche(b.itemCode),
  )

  return (
    <section className="bail">
      <nav className="fil-ariane" aria-label={t.annonceFilAriane}>
        <Link to="/mes-baux">{t.mesBaux}</Link>
        <Icone nom="chevron" taille="sm" miroir />
        <span aria-current="page">{bien}</span>
      </nav>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}

      <div className="fiche__colonnes">
        <div className="pile pile--large">
          <Bloc
            titre={<h2>{t.bailContrat}</h2>}
            actions={
              <Puce ton={tonStatutBail(bail.status)}>
                {bail.status === 'Active'
                  ? interpoler(t.bailActifDepuis, { date: jour(langue, bail.startDate) })
                  : libelleStatutBail(t, bail.status)}
              </Puce>
            }
          >
            <div className="pile">
              {jeDoisConfirmer && (
                <div className="bloc bloc--sourd pile pile--serree">
                  <Bandeau ton="attention" icone={<Icone nom="alerte" />}>
                    <b>{t.bailSignatureAttendue}</b> {t.bailConfirmerAide}
                  </Bandeau>
                  <div>
                    <Bouton charge={occupe === 'confirmer'} onClick={() => void agir('confirmer', () => confirmerBail(id))}>
                      {t.bailConfirmerSignature}
                    </Bouton>
                  </div>
                </div>
              )}

              {/*
                Le bailleur déclare la date portée sur le papier. Ce n'est pas une signature
                électronique et l'écran ne le laisse pas croire : le bail attend ensuite chaque
                locataire (conception L6, §1.4).
              */}
              {estBailleur && bail.status === 'Draft' && (
                <form
                  className="bloc bloc--sourd bail__signature"
                  onSubmit={(evenement) => {
                    evenement.preventDefault()
                    void agir('signer', () => declarerSigne(id, dateSignature))
                  }}
                >
                  <Champ
                    id="bail-signe-le"
                    type="date"
                    libelle={t.bailDateSignature}
                    valeur={dateSignature}
                    aide={t.bailDeclarerSigneAide}
                    requis
                    onChange={setDateSignature}
                  />
                  <div>
                    <Bouton type="submit" charge={occupe === 'signer'}>
                      {t.bailDeclarerSigne}
                    </Bouton>
                  </div>
                </form>
              )}

              {estBailleur && bail.status === 'PendingSignature' && (
                <Bandeau ton="info" icone={<Icone nom="horloge" />}>
                  {interpoler(t.mesBauxConfirmations, { n: bail.confirmedTenantCount, total: bail.tenantCount }, langue)}
                </Bandeau>
              )}

              <dl className="fiche__lignes">
                <div className="fiche__ligne">
                  <dt>{t.bailType}</dt>
                  <dd>{libelleTypeBail(t, bail)}</dd>
                </div>
                <div className="fiche__ligne">
                  <dt>{t.bailPeriode}</dt>
                  <dd>{periode(langue, bail.startDate, bail.endDate)}</dd>
                </div>
                <div className="fiche__ligne">
                  <dt>{t.quittanceLoyerHorsCharges}</dt>
                  <dd>
                    <bdi>{montant(langue, bail.rentAmount, bail.currencyCode)}</bdi>
                  </dd>
                </div>
                <div className="fiche__ligne">
                  <dt>{t.bailChargesForfaitaires}</dt>
                  <dd>
                    <bdi>{montant(langue, bail.chargesAmount, bail.currencyCode)}</bdi>
                  </dd>
                </div>
                <div className="fiche__ligne">
                  <dt>{t.bailDepot}</dt>
                  <dd>
                    <bdi>{montant(langue, detail.securityDeposit, bail.currencyCode)}</bdi>
                    {Number.isInteger(moisDepot) && moisDepot > 0 && (
                      <span className="secondaire"> ({interpoler(t.bailDepotMois, { n: moisDepot }, langue)})</span>
                    )}
                  </dd>
                </div>
                <div className="fiche__ligne">
                  <dt>{t.bailEcheanceMensuelle}</dt>
                  <dd>{interpoler(t.mesBauxJourEcheance, { jour: bail.paymentDueDayOfMonth }, langue)}</dd>
                </div>
                <div className="fiche__ligne">
                  <dt>{t.bailPreavis}</dt>
                  <dd>{interpoler(t.mesBauxPreavisJours, { n: detail.noticePeriodDays }, langue)}</dd>
                </div>
                {detail.signedOn !== null && (
                  <div className="fiche__ligne">
                    <dt>{t.bailSigneLe}</dt>
                    <dd>{jour(langue, detail.signedOn)}</dd>
                  </div>
                )}
              </dl>
            </div>
          </Bloc>

          {/*
            La signature est bilatérale (conception L6, §1.4) : l'écran dit QUI a confirmé et qui
            manque. Un « en attente » sans nom ne dit pas à qui aller parler.
          */}
          <Bloc titre={<h2>{t.bailParties}</h2>}>
            <ul className="liste-lignes">
              {detail.parties.map((partie) => (
                <LignePartie key={partie.id} t={t} langue={langue} partie={partie} moi={moi} signedOn={detail.signedOn} />
              ))}
            </ul>
          </Bloc>

          <Bloc titre={<h2>{t.bailDemarches}</h2>}>
            <p className="secondaire bail__intro">{t.bailDemarchesIntro}</p>
            <ul className="liste-lignes">
              {demarches.map((demarche) => (
                <LigneDemarche
                  key={demarche.itemCode}
                  t={t}
                  langue={langue}
                  demarche={demarche}
                  auteur={nomDeLAuteur(t, detail.parties, demarche.completedByUserId, moi)}
                  occupe={occupe === demarche.itemCode}
                  onMarquer={(statut) =>
                    void agir(demarche.itemCode, () => marquerDemarche(id, demarche.itemCode, statut, null))
                  }
                />
              ))}
            </ul>
          </Bloc>
        </div>

        <aside className="fiche__aparte pile">
          <Bloc titre={<h3>{t.bailDocuments}</h3>}>
            <div className="pile">
              {detail.hasGeneratedModel ? (
                <Bouton
                  apparence="secondaire"
                  bloc
                  charge={occupe === 'telecharger'}
                  onClick={() => void telechargerSansRecharger('telecharger', () => telechargerModeleBail(id))}
                >
                  <Icone nom="telechargement" taille="sm" /> {t.bailModeleTelecharger}
                </Bouton>
              ) : (
                <Bouton
                  apparence="secondaire"
                  bloc
                  charge={occupe === 'modele'}
                  onClick={() => void agir('modele', () => engendrerModele(id, langue))}
                >
                  {t.bailModeleEngendrer}
                </Bouton>
              )}

              {detail.generatedModel !== null && (
                <p className="tertiaire">
                  {interpoler(t.bailModeleEngendreLe, {
                    date: jour(langue, detail.generatedModel.generatedAt),
                    langue: nomDeLangue(detail.generatedModel.language),
                  })}
                </p>
              )}

              {detail.generatedModel?.isProvisional === true && (
                <Bandeau ton="attention" icone={<Icone nom="alerte" />}>
                  <b>{t.bailModeleProvisoireTitre}</b> {t.bailModeleProvisoireAide}
                </Bandeau>
              )}

              {detail.hasGeneratedModel && (
                <div>
                  <Bouton
                    apparence="discret"
                    taille="sm"
                    charge={occupe === 'modele'}
                    onClick={() => void agir('modele', () => engendrerModele(id, langue))}
                  >
                    {t.bailModeleEngendrerANouveau}
                  </Bouton>
                </div>
              )}

              <p className="tertiaire">{t.bailPasPartieAuContrat}</p>

              <hr className="bail__filet" />

              <LienBouton to="/quittances" apparence="discret" bloc>
                {t.quittances}
              </LienBouton>
              <LienBouton to={`/echeances?bail=${id}`} apparence="discret" bloc>
                {t.echeancier}
              </LienBouton>
            </div>
          </Bloc>

          {/*
            On annonce SON départ, et rien d'autre (conception L7, §1.10-a) : le geste vit dans
            l'agenda du foyer, la fiche y mène. Le bailleur, lui, lit le terme et le préavis.
          */}
          {bail.status === 'Active' && (
            <Bloc titre={<h3>{t.bailFin}</h3>}>
              <div className="pile pile--serree">
                <p className="secondaire">
                  {interpoler(t.bailFinAide, { date: jour(langue, bail.endDate), n: detail.noticePeriodDays }, langue)}
                </p>
                {!estBailleur && detail.householdId !== null && (
                  <>
                    <p className="secondaire">{t.foyerDepartAide}</p>
                    <LienBouton to="/foyer?onglet=depart" apparence="secondaire" bloc>
                      {t.foyerAnnoncerDepart}
                    </LienBouton>
                  </>
                )}
              </div>
            </Bloc>
          )}
        </aside>
      </div>
    </section>
  )
}

/** Une partie au bail : qui, à quel titre, et où elle en est de la signature. */
function LignePartie({
  t,
  langue,
  partie,
  moi,
  signedOn,
}: {
  t: (typeof TEXTES)['fr']
  langue: Langue
  partie: PartieAuBail
  moi: string | null
  signedOn: string | null
}) {
  const role =
    partie.role === 'Landlord' ? t.bailRoleBailleur : partie.role === 'Mandatary' ? t.bailRoleMandataire : t.bailRoleLocataire
  const meta = partie.userId === moi ? `${role} — ${t.bailVous}` : role

  return (
    <li className={partie.leftOn !== null ? 'est-estompee' : undefined}>
      <span className="avatar avatar--sm" aria-hidden="true">
        {initiales(partie.displayName)}
      </span>
      <span className="liste-lignes__corps">
        <span className="liste-lignes__titre">{partie.displayName ?? '—'}</span>
        <span className="liste-lignes__meta">{meta}</span>
      </span>
      <EtatPartie t={t} langue={langue} partie={partie} signedOn={signedOn} />
    </li>
  )
}

/**
 * L'état d'une partie. Le bailleur ne confirme pas : il déclare, et sa date est celle du papier
 * (`signedOn`). Un locataire confirme, et sa date est celle du geste.
 */
function EtatPartie({
  t,
  langue,
  partie,
  signedOn,
}: {
  t: (typeof TEXTES)['fr']
  langue: Langue
  partie: PartieAuBail
  signedOn: string | null
}) {
  if (partie.leftOn !== null) {
    return <span className="statut-point">{interpoler(t.bailPartiePartieLe, { date: jour(langue, partie.leftOn) })}</span>
  }

  if (partie.role !== 'Tenant') {
    return signedOn === null ? (
      <span className="statut-point statut-point--attente">{t.bailPartieADeclarer}</span>
    ) : (
      <span className="statut-point statut-point--succes">{interpoler(t.bailPartieSigneLe, { date: jour(langue, signedOn) })}</span>
    )
  }

  return partie.confirmedAt === null ? (
    <span className="statut-point statut-point--attente">{t.bailPartieEnAttente}</span>
  ) : (
    <span className="statut-point statut-point--succes">
      {interpoler(t.bailPartieConfirmeeLe, { date: jour(langue, partie.confirmedAt) })}
    </span>
  )
}

/**
 * Une démarche : une case qui dit l'état, une méta qui dit le fait, et le geste qui manque.
 *
 * La case reste active dans les deux sens — décocher rend la démarche « non déclarée », parce
 * qu'une erreur de clic se corrige. « Sans objet » est un troisième état, pas une case décochée :
 * l'enregistrement, par exemple, est recommandé et non imposé.
 */
function LigneDemarche({
  t,
  langue,
  demarche,
  auteur,
  occupe,
  onMarquer,
}: {
  t: (typeof TEXTES)['fr']
  langue: Langue
  demarche: DemarcheBail
  auteur: string
  occupe: boolean
  onMarquer: (statut: StatutDemarche) => void
}) {
  const libelle = libelleDemarche(t, demarche.itemCode)
  const faite = demarche.status === 'Done'
  let meta: string

  if (faite) {
    meta =
      demarche.completedAt === null
        ? t.bailDemarcheFaite
        : interpoler(t.bailDemarcheDeclaree, { qui: auteur, date: jour(langue, demarche.completedAt) })
  } else if (demarche.status === 'NotApplicable') {
    meta = t.bailDemarcheSansObjet
  } else {
    meta = t.bailDemarcheNonDeclaree
  }

  return (
    <li>
      <input
        type="checkbox"
        className="bail__case"
        checked={faite}
        disabled={occupe}
        aria-label={libelle}
        onChange={(evenement) => onMarquer(evenement.target.checked ? 'Done' : 'Pending')}
      />
      <span className="liste-lignes__corps">
        <span className="liste-lignes__titre">{libelle}</span>
        <span className="liste-lignes__meta">
          {meta}
          {demarche.note !== null && ` — ${demarche.note}`}
        </span>
      </span>
      {demarche.status === 'Pending' && (
        <span className="bail__gestes">
          <Bouton apparence="discret" taille="sm" charge={occupe} onClick={() => onMarquer('Done')}>
            {t.bailDemarcheDeclarer}
          </Bouton>
          <Bouton apparence="discret" taille="sm" desactive={occupe} onClick={() => onMarquer('NotApplicable')}>
            {t.bailDemarcheSansObjet}
          </Bouton>
        </span>
      )}
      {demarche.status === 'NotApplicable' && (
        <Bouton apparence="discret" taille="sm" charge={occupe} onClick={() => onMarquer('Pending')}>
          {t.bailDemarcheRetablir}
        </Bouton>
      )}
    </li>
  )
}

/** « vous », « le bailleur », ou le nom : l'auteur d'une démarche est toujours une partie au bail. */
function nomDeLAuteur(
  t: (typeof TEXTES)['fr'],
  parties: PartieAuBail[],
  userId: string | null,
  moi: string | null,
): string {
  if (userId === null) return '—'
  if (userId === moi) return t.bailDemarcheParVous

  const partie = parties.find((candidate) => candidate.userId === userId)

  if (partie === undefined) return '—'
  if (partie.role === 'Landlord') return t.bailDemarcheParBailleur

  return partie.displayName ?? '—'
}

/** L'ordre de la loi : le contrat, l'état des lieux, l'enregistrement, le dépôt. L'API trie par code. */
function rangDemarche(code: string): number {
  const rang = (DEMARCHES as readonly string[]).indexOf(code)

  return rang === -1 ? DEMARCHES.length : rang
}

/** Deux lettres pour l'avatar : « Mehdi Tazi » → « MT ». */
function initiales(nom: string | null): string {
  if (nom === null) return '?'

  return nom
    .split(/\s+/)
    .filter((mot) => mot.length > 0)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toLocaleUpperCase())
    .join('')
}

/** Le nom d'une langue dans cette langue ; un code inconnu reste un code. Exporté : les quittances l'utilisent. */
export function nomDeLangue(code: string): string {
  return (NOMS_DE_LANGUE as Record<string, string>)[code] ?? code
}

/**
 * « Individuel par chambre · solidaire » ou « Unique solidaire » : c'est le bail par chambre qui
 * a deux cas, l'autre dit déjà la solidarité dans son nom.
 */
export function libelleTypeBail(t: (typeof TEXTES)['fr'], bail: Bail): string {
  return bail.type === 'Individual'
    ? `${t.bailIndividuel} · ${bail.isJointlyLiable ? t.mesBauxSolidaire : t.mesBauxNonSolidaire}`
    : t.bailSolidaire
}

/**
 * Mes baux, en une ligne chacun — la suite de « bail introuvable ».
 *
 * Une liste de liens, pas les cartes de « Mes baux » : on cherche lequel on voulait, pas à agir.
 * Rien ne s'affiche sans bail, ni tant qu'ils n'arrivent pas : l'état d'erreur est déjà à l'écran.
 */
function MesBauxResume({ moi }: { moi: string | null }) {
  const t = useTextes()
  const langue = useLangue()
  const ville = useVille()
  const [liste, setListe] = useState<Bail[] | null>(null)

  useEffect(() => {
    void Promise.all([mesBaux(false), mesBaux(true)])
      .then((pages) => {
        const vus = new Map<string, Bail>()

        for (const page of pages) {
          for (const bail of page.items) {
            vus.set(bail.id, bail)
          }
        }

        setListe([...vus.values()].sort((a, b) => rang(a) - rang(b) || b.startDate.localeCompare(a.startDate)))
      })
      .catch(() => setListe([]))
  }, [])

  if (liste === null || liste.length === 0) {
    return null
  }

  return (
    <div className="erreur__suite">
      <h2>{t.erreurBailSuite}</h2>
      <ul className="erreur__baux">
        {liste.map((bail) => (
          <li key={bail.id}>
            <Link to={`/baux/${bail.id}`}>
              <span className="erreur__baux-corps">
                <span className="erreur__baux-titre">{libelleBien(bail, ville.libelle)}</span>
                <span className="erreur__baux-meta">
                  {moi !== null && bail.landlordUserId === moi ? t.erreurBailBailleur : t.erreurBailLocataire}
                  {' · '}
                  {periode(langue, bail.startDate, bail.endDate)}
                </span>
              </span>
              <Puce ton={tonStatutBail(bail.status)}>{libelleStatutBail(t, bail.status)}</Puce>
              <Icone nom="chevron" taille="sm" miroir />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Le statut d'un bail, en clair. Exporté : l'échéancier le met dans son sélecteur de bail. */
export function libelleStatutBail(t: (typeof TEXTES)['fr'], statut: Bail['status']): string {
  switch (statut) {
    case 'Draft':
      return t.bailStatutDraft
    case 'PendingSignature':
      return t.bailStatutPendingSignature
    case 'Active':
      return t.bailStatutActive
    case 'Terminated':
      return t.bailStatutTerminated
    case 'Expired':
      return t.bailStatutExpired
    case 'Cancelled':
      return t.bailStatutCancelled
    default:
      return statut
  }
}

function libelleDemarche(t: (typeof TEXTES)['fr'], code: string): string {
  switch (code) {
    case 'WrittenContract':
      return t.demarcheWrittenContract
    case 'EntryInventory':
      return t.demarcheEntryInventory
    case 'Registration':
      return t.demarcheRegistration
    case 'DepositCap':
      return t.demarcheDepositCap
    default:
      return code
  }
}
